import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createContext, use, useState } from 'react';
import { extractAriaProps, extractDataProps } from '@famly/mf_ts-utils_data-aria-props';
import { Dialog } from '@famly/mf_modals-dialogs_dialog';
import { Modal, SplitModal } from '@famly/mf_modals-dialogs_modal';
import { Drawer } from '@famly/mf_navigation_drawer';
import { preloadMarkdownDescription } from './markdown-description';
export const ModalDialogContext = createContext(undefined);
/**
 * Provider component that manages modal/dialog state
 *
 * @param children - React children nodes
 *
 * @example
 * <ModalDialogProvider>
 *   <App />
 * </ModalDialogProvider>
 */
export const ModalDialogProvider = ({ children }) => {
    const [modalDialogs, setModalDialogs] = useState([]);
    // Memoized by hand: the signin app and Jest do not run the React Compiler, and consumers close
    // their modal in effects keyed on these functions, so a new identity would close it at once.
    const showModalDialog = React.useCallback((modalDialog) => {
        // instead of cleaning the last closed modal up by timeout or other fancy hacks,
        // all the not currently open modals are filtered out when opening a new modal.
        // Ids must stay unique — `hideModalDialog` closes only the first match — so a same-id
        // entry, always the same call site showing again, is replaced without running teardown.
        setModalDialogs(prev => prev.filter(p => p.open && p.id !== modalDialog.id).concat({ ...modalDialog, open: true }));
    }, []);
    // The provider mounts with the app, so the renderer loads once, after the first render.
    React.useEffect(() => {
        preloadMarkdownDescription();
    }, []);
    const hideModalDialog = React.useCallback((id) => {
        setModalDialogs(prev => 
        // Hiding an id that is not open leaves the list as it is, so a caller hiding from an effect cannot loop.
        prev.some(modalDialog => modalDialog.id === id && modalDialog.open)
            ? prev.map(modalDialog => (modalDialog.id === id ? { ...modalDialog, open: false } : modalDialog))
            : prev);
    }, []);
    return (_jsxs(ModalDialogContext, { value: React.useMemo(() => ({ modalDialogs, showModalDialog, hideModalDialog }), [hideModalDialog, modalDialogs, showModalDialog]), children: [children, modalDialogs.map((modalDialog, index) => {
                if (modalDialog.type === 'dialog') {
                    return (_jsx(Dialog, { open: modalDialog.open, onClose: modalDialog.onClose, ...extractDataProps(modalDialog), ...extractAriaProps(modalDialog), children: modalDialog.element }, modalDialog.id));
                }
                if (modalDialog.type === 'drawer') {
                    // A drawer shown while a modal or dialog is open was opened from it, so it has to
                    // paint in front of that modal rather than behind its backdrop.
                    const aboveModals = modalDialogs
                        .slice(0, index)
                        .some(earlier => earlier.open && earlier.type !== 'drawer');
                    return (_jsx(Drawer, { open: modalDialog.open, title: modalDialog.title, maxWidth: modalDialog.maxWidth, aboveModals: aboveModals, onClose: () => modalDialog.onClose(), children: modalDialog.element }, modalDialog.id));
                }
                if (modalDialog.size === 'split') {
                    const splitModalDialog = modalDialog;
                    return (_jsx(SplitModal, { open: modalDialog.open, variant: splitModalDialog.variant, gridTemplateColumns: splitModalDialog.gridTemplateColumns, fullScreen: splitModalDialog.fullScreen, onClose: modalDialog.onClose, ...extractDataProps(modalDialog), ...extractAriaProps(modalDialog), children: modalDialog.element }, modalDialog.id));
                }
                return (_jsx(Modal, { open: modalDialog.open, size: modalDialog.size, zIndex: modalDialog.zIndex, onClose: modalDialog.onClose, ...extractDataProps(modalDialog), ...extractAriaProps(modalDialog), children: modalDialog.element }, modalDialog.id));
            })] }));
};
/**
 * Hook to access modal dialog context
 *
 * @returns {ModalDialogContextType} Modal dialog context
 * @throws Error if used outside provider
 *
 * @example
 * const { showModalDialog, hideModalDialog } = useModalsDialogsContext()
 */
export const useModalsDialogsContext = () => {
    const context = use(ModalDialogContext);
    if (!context) {
        throw new Error('useModalsDialogsContext must be used within a ModalProvider');
    }
    return context;
};
